import React, {useEffect, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import ProjectDetailsUI from '../view/ProjectDetailsUI';
import ProjectsStore from '../../store/ProjectsStore';
import ListCoursesStore from '../../../Courses/List/store/ListCoursesStore';
import MicroreelsListStore from '../../../Microreels/List/store/MicroreelsListStore';
import PostsStore from '../../../Posts/store/PostsStore';
import NavigationStore from '../../../../navigation/store/NavigationStore';
import {Project} from '../../../../data/models/Projects/Project';
import {Course, CourseListItem} from '../../../../data/models/Course/Course';
import {MicroreelGroup} from '../../../../data/models/Microreels/MicroreelGroup';
import {
  POST_CAPTION_MAX_LENGTH,
  PostListItem,
} from '../../../../data/models/Posts/Post';
import {APP_PERMISSIONS} from '../../../../data/permissions';
import ConfirmationModal from '../../../../uiToolkit/ConfirmationModal';
import {toastConfig} from '../../../../uiToolkit/Toast/toastConfig';
import ModalFade, {
  useModalFade,
} from '../../../Microreels/List/components/ModalFade';
import EditMicroreelInfoModal from '../../../Microreels/List/components/EditMicroreelInfoModal';
import MicroreelLearnersModal from '../../../Microreels/List/components/MicroreelLearnersModal';
import {microreelsListText} from '../../../Microreels/List/microreelsListText';
import {groupPosterText} from '../../../Microreels/List/groupPosterText';
import {coursesListText} from '../../../Courses/List/coursesListText';
import {postsText} from '../../../Posts/postsText';
import {postPosterText} from '../../../Posts/postPosterText';
import ProjectFormModal from '../../components/ProjectFormModal';
import {ProjectTab} from '../../components/ProjectContentToggle';
import MoveToProjectModal from '../../components/MoveToProjectModal';
import {projectsText} from '../../projectsText';

interface Props {
  projectsStore?: ProjectsStore;
  listCoursesStore?: ListCoursesStore;
  microreelsListStore?: MicroreelsListStore;
  postsStore?: PostsStore;
  navigationStore?: NavigationStore;
}

const ProjectDetailsContainer = inject(
  'projectsStore',
  'listCoursesStore',
  'microreelsListStore',
  'postsStore',
  'navigationStore',
)(
  observer(
    ({
      projectsStore,
      listCoursesStore,
      microreelsListStore,
      postsStore,
      navigationStore,
    }: Props) => {
      const {organizationId, projectId} = useParams();
      const navigate = useNavigate();
      const [searchParams, setSearchParams] = useSearchParams();
      const tabParam = searchParams.get('tab');
      const tab: ProjectTab =
        tabParam === 'microreels' || tabParam === 'posts'
          ? tabParam
          : 'courses';
      const [project, setProject] = useState<Project>();
      const [courses, setCourses] = useState<CourseListItem[]>([]);
      const [coursesPage, setCoursesPage] = useState(0);
      const [coursesPagesCount, setCoursesPagesCount] = useState(0);
      const [coursesLoaded, setCoursesLoaded] = useState(false);
      const [groups, setGroups] = useState<MicroreelGroup[]>([]);
      const [groupsPage, setGroupsPage] = useState(0);
      const [groupsPagesCount, setGroupsPagesCount] = useState(0);
      const [groupsLoaded, setGroupsLoaded] = useState(false);
      const [posts, setPosts] = useState<PostListItem[]>([]);
      const [postsPage, setPostsPage] = useState(0);
      const [postsPagesCount, setPostsPagesCount] = useState(0);
      const [postsLoaded, setPostsLoaded] = useState(false);
      const [renameShown, setRenameShown] = useState(false);
      const [deleteShown, setDeleteShown] = useState(false);
      const [courseToCopy, setCourseToCopy] = useState<string>();
      const [courseToDelete, setCourseToDelete] = useState<string>();
      const [itemToMove, setItemToMove] = useState<{
        id: string;
        projectId?: string | null;
      }>();
      const [groupToEditId, setGroupToEditId] = useState<string>();
      const [groupToEdit, setGroupToEdit] = useState<Course>();
      const [groupToDelete, setGroupToDelete] = useState<MicroreelGroup>();
      const [postToEdit, setPostToEdit] = useState<PostListItem>();
      const [postCourseToEdit, setPostCourseToEdit] = useState<Course>();
      const [postToDelete, setPostToDelete] = useState<PostListItem>();
      const [learnersPost, setLearnersPost] = useState<PostListItem>();

      const deleteFade = useModalFade(deleteShown);
      const editGroupFade = useModalFade(groupToEditId !== undefined);
      const deleteGroupFade = useModalFade(!!groupToDelete);
      const editPostFade = useModalFade(!!postToEdit);
      const deletePostFade = useModalFade(!!postToDelete);

      const permissions = navigationStore?.userPermissions || [];
      const canManage = permissions.includes(
        APP_PERMISSIONS.ORG_CREATE_COURSES,
      );
      const projectPath = `/manage/${organizationId}/projects/${projectId}?tab=${tab}`;

      const loadProject = () => {
        if (!organizationId || !projectId) return;
        projectsStore!.getProject(organizationId, projectId).then(result => {
          if (result) {
            setProject(result);
          } else {
            navigate(`/manage/${organizationId}/projects`);
          }
        });
      };

      const fetchCourses = () => {
        if (!organizationId || !projectId) return;
        projectsStore!
          .getProjectCourses(organizationId, projectId, 0)
          .then(res => {
            setCourses(res.data);
            setCoursesPage(0);
            setCoursesPagesCount(res.pagesCount);
            setCoursesLoaded(true);
          });
      };

      const fetchGroups = () => {
        if (!organizationId || !projectId) return;
        projectsStore!
          .getProjectMicroreels(organizationId, projectId, 0)
          .then(res => {
            setGroups(res.data);
            setGroupsPage(0);
            setGroupsPagesCount(res.pagesCount);
            setGroupsLoaded(true);
          });
      };

      const fetchPosts = () => {
        if (!organizationId || !projectId) return;
        postsStore!.getPosts(organizationId, 0, '', {projectId}).then(res => {
          setPosts(res.data);
          setPostsPage(0);
          setPostsPagesCount(res.pagesCount);
          setPostsLoaded(true);
        });
      };

      useEffect(() => {
        setCoursesLoaded(false);
        setGroupsLoaded(false);
        setPostsLoaded(false);
        loadProject();
        fetchCourses();
        fetchGroups();
        fetchPosts();
      }, [organizationId, projectId]);

      useEffect(() => {
        if (!organizationId || !postToEdit) {
          setPostCourseToEdit(undefined);
          return;
        }
        postsStore!
          .getPostCourse(organizationId, postToEdit.id)
          .then(course => {
            if (course) {
              setPostCourseToEdit(course);
            } else {
              setPostToEdit(undefined);
            }
          });
      }, [postToEdit, organizationId]);

      useEffect(() => {
        if (!organizationId || !groupToEditId) {
          setGroupToEdit(undefined);
          return;
        }
        microreelsListStore!
          .getGroup(organizationId, groupToEditId)
          .then(course => {
            if (course) {
              setGroupToEdit(course);
            } else {
              setGroupToEditId(undefined);
            }
          });
      }, [groupToEditId, organizationId]);

      const loadNextPage = async () => {
        if (!organizationId || !projectId) return;
        if (tab === 'courses') {
          if (coursesPage + 1 >= coursesPagesCount) return;
          const res = await projectsStore!.getProjectCourses(
            organizationId,
            projectId,
            coursesPage + 1,
          );
          setCourses(current => [...current, ...res.data]);
          setCoursesPage(coursesPage + 1);
        } else if (tab === 'microreels') {
          if (groupsPage + 1 >= groupsPagesCount) return;
          const res = await projectsStore!.getProjectMicroreels(
            organizationId,
            projectId,
            groupsPage + 1,
          );
          setGroups(current => [...current, ...res.data]);
          setGroupsPage(groupsPage + 1);
        } else {
          if (postsPage + 1 >= postsPagesCount) return;
          const res = await postsStore!.getPosts(
            organizationId,
            postsPage + 1,
            '',
            {projectId},
          );
          setPosts(current => [...current, ...res.data]);
          setPostsPage(postsPage + 1);
        }
      };

      const onRename = async (name: string) => {
        if (!organizationId || !projectId) return;
        const success = await projectsStore!.editProject(
          organizationId,
          projectId,
          name,
        );
        if (success) {
          setRenameShown(false);
          loadProject();
        }
      };

      const onConfirmDelete = async () => {
        if (!organizationId || !projectId) return;
        const success = await projectsStore!.deleteProject(
          organizationId,
          projectId,
        );
        if (success) {
          navigate(`/manage/${organizationId}/projects`);
        }
      };

      const onConfirmCopyCourse = () => {
        if (!organizationId || !courseToCopy) return;
        listCoursesStore!.copyCourse(organizationId, courseToCopy).then(() => {
          setCourseToCopy(undefined);
          fetchCourses();
        });
      };

      const onConfirmDeleteCourse = () => {
        if (!organizationId || !courseToDelete) return;
        listCoursesStore!
          .deleteCourse(organizationId, courseToDelete)
          .then(success => {
            if (success) {
              toast.success(coursesListText.courseDeleted, toastConfig);
              fetchCourses();
              loadProject();
            }
            setCourseToDelete(undefined);
          });
      };

      const onMoveCourse = (courseId: string) => {
        const course = courses.find(item => item.id === courseId);
        setItemToMove({
          id: courseId,
          projectId: course?.projectId ?? projectId,
        });
      };

      const onMoveGroup = (group: MicroreelGroup) => {
        setItemToMove({id: group.id, projectId: group.projectId ?? projectId});
      };

      const onMovePost = (post: PostListItem) => {
        setItemToMove({id: post.id, projectId: post.projectId ?? projectId});
      };

      const onMoved = () => {
        setItemToMove(undefined);
        loadProject();
        fetchCourses();
        fetchGroups();
        fetchPosts();
      };

      const onOpenPost = (post: PostListItem) => {
        navigate(`/manage/${organizationId}/posts/${post.id}`, {
          state: {backPath: projectPath},
        });
      };

      const onPostAnalytics = (post: PostListItem) => {
        if (!post.publishedCourseId) return;
        navigate(
          `/manage/${organizationId}/analytics/course/${post.publishedCourseId}/learners`,
          {state: {backPath: projectPath}},
        );
      };

      const onSavePost = async (
        name: string,
        description: string,
        cover?: File,
      ) => {
        if (!organizationId || !postCourseToEdit) return;
        const success = await postsStore!.editPost(
          organizationId,
          {...postCourseToEdit, title: name, description},
          postToEdit?.lessonId,
          cover,
        );
        if (success) {
          setPostToEdit(undefined);
          fetchPosts();
        }
      };

      const onConfirmDeletePost = async () => {
        if (!organizationId || !postToDelete) return;
        const success = await postsStore!.deletePost(
          organizationId,
          postToDelete.id,
        );
        setPostToDelete(undefined);
        if (success) {
          fetchPosts();
          loadProject();
        }
      };

      const onOpenGroup = (group: MicroreelGroup) => {
        navigate(`/manage/${organizationId}/microreels/${group.id}`, {
          state: {backPath: projectPath},
        });
      };

      const onGroupCourseEditor = (group: MicroreelGroup) => {
        const folderId = group.folders?.[0]?.id;
        navigate(
          `/editor/${organizationId}/courses/${group.id}/folders/${folderId}/lessons`,
        );
      };

      const onGroupAnalytics = (group: MicroreelGroup) => {
        navigate(
          `/manage/${organizationId}/analytics/course/${group.publishedCourseId}/learners`,
          {state: {backPath: projectPath}},
        );
      };

      const onCancelEditGroup = () => {
        setGroupToEditId(undefined);
        setGroupToEdit(undefined);
      };

      const onSaveGroup = async (
        name: string,
        description: string,
        cover?: File,
      ) => {
        if (!organizationId || !groupToEdit?.id) return;
        const updated: Course = {...groupToEdit, title: name, description};
        const success = await microreelsListStore!.editGroup(
          organizationId,
          updated,
          cover,
        );
        if (success) {
          await microreelsListStore!.republishGroupSilent(
            organizationId,
            groupToEdit.id,
            {ignoreNotPublished: true},
          );
          fetchGroups();
          onCancelEditGroup();
        }
      };

      const onConfirmDeleteGroup = () => {
        if (!organizationId || !groupToDelete) return;
        microreelsListStore!
          .deleteGroup(organizationId, groupToDelete.id)
          .then(success => {
            if (success) {
              fetchGroups();
              loadProject();
            }
            setGroupToDelete(undefined);
          });
      };

      return (
        <>
          <ProjectDetailsUI
            project={project}
            canManage={canManage}
            permissions={permissions}
            tab={tab}
            onChangeTab={value =>
              setSearchParams({tab: value}, {replace: true})
            }
            courses={courses}
            coursesLoaded={coursesLoaded}
            groups={groups}
            groupsLoaded={groupsLoaded}
            onBack={() => navigate(`/manage/${organizationId}/projects`)}
            onRename={() => setRenameShown(true)}
            onDelete={() => setDeleteShown(true)}
            onCreateCourse={() =>
              navigate(`/manage/${organizationId}/courses/create`, {
                state: {projectId, projectName: project?.name},
              })
            }
            onCreateMicroreels={() =>
              navigate(`/manage/${organizationId}/home/generate_microreel`, {
                state: {projectId, projectName: project?.name},
              })
            }
            onCreatePost={() =>
              navigate(`/manage/${organizationId}/home/create_post`, {
                state: {projectId, projectName: project?.name},
              })
            }
            loadNextPage={loadNextPage}
            onEditCourse={courseId =>
              navigate(`/manage/${organizationId}/courses/${courseId}/general`)
            }
            onOpenCourseFolders={courseId =>
              navigate(`/editor/${organizationId}/courses/${courseId}/folders`)
            }
            onPreviewCourse={courseId =>
              navigate(`/preview/${organizationId}/course/${courseId}`)
            }
            onCopyCourse={setCourseToCopy}
            onDeleteCourse={setCourseToDelete}
            onMoveCourse={onMoveCourse}
            onOpenGroup={onOpenGroup}
            onGroupCourseEditor={onGroupCourseEditor}
            onEditGroup={group => setGroupToEditId(group.id)}
            onDeleteGroup={setGroupToDelete}
            onGroupAnalytics={onGroupAnalytics}
            onMoveGroup={onMoveGroup}
            posts={posts}
            postsLoaded={postsLoaded}
            onOpenPost={onOpenPost}
            onEditPost={setPostToEdit}
            onPostLearners={setLearnersPost}
            onPostAnalytics={onPostAnalytics}
            onDeletePost={setPostToDelete}
            onMovePost={onMovePost}
          />
          {renameShown && project && (
            <ProjectFormModal
              project={project}
              isLoading={projectsStore!.isLoading}
              onSubmit={onRename}
              onClose={() => setRenameShown(false)}
            />
          )}
          {deleteFade.rendered && (
            <ModalFade closing={deleteFade.closing}>
              <ConfirmationModal
                shown
                title={projectsText.deleteConfirm({
                  coursesNumber: project?.coursesNumber || 0,
                  microreelsNumber: project?.microreelsNumber || 0,
                  postsNumber: project?.postsNumber || 0,
                })}
                acceptText={projectsText.delete}
                onAccept={onConfirmDelete}
                onCancel={() => setDeleteShown(false)}
              />
            </ModalFade>
          )}
          <ConfirmationModal
            shown={courseToCopy !== undefined}
            onAccept={onConfirmCopyCourse}
            onCancel={() => setCourseToCopy(undefined)}
            title={coursesListText.copyConfirm}
          />
          <ConfirmationModal
            shown={courseToDelete !== undefined}
            onAccept={onConfirmDeleteCourse}
            onCancel={() => setCourseToDelete(undefined)}
            title={coursesListText.deleteConfirm}
          />
          {itemToMove && (
            <MoveToProjectModal
              courseId={itemToMove.id}
              currentProjectId={itemToMove.projectId}
              onClose={() => setItemToMove(undefined)}
              onMoved={onMoved}
            />
          )}
          {editGroupFade.rendered && (
            <ModalFade closing={editGroupFade.closing}>
              <EditMicroreelInfoModal
                shown
                headerTitle={microreelsListText.editGroupInfoTitle}
                initialName={groupToEdit?.title}
                initialDescription={groupToEdit?.description}
                posterText={groupPosterText}
                posterAspectRatio="16 / 9"
                initialCover={groupToEdit?.image}
                isLoading={microreelsListStore!.isEditLoading}
                onCancel={onCancelEditGroup}
                onSave={onSaveGroup}
              />
            </ModalFade>
          )}
          {deleteGroupFade.rendered && (
            <ModalFade closing={deleteGroupFade.closing}>
              <ConfirmationModal
                shown
                onAccept={onConfirmDeleteGroup}
                onCancel={() => setGroupToDelete(undefined)}
                title={microreelsListText.deleteGroup}
                acceptText={microreelsListText.delete}
              />
            </ModalFade>
          )}
          {editPostFade.rendered && (
            <ModalFade closing={editPostFade.closing}>
              <EditMicroreelInfoModal
                shown
                headerTitle={postsText.editInfoTitle}
                initialName={postCourseToEdit?.title}
                initialDescription={postCourseToEdit?.description}
                isLoading={postsStore!.isEditLoading}
                onCancel={() => setPostToEdit(undefined)}
                namePlaceholder={postsText.postTitlePlaceholder}
                descriptionField={postsText.caption}
                descriptionPlaceholder={postsText.captionPlaceholder}
                maxDescriptionLength={POST_CAPTION_MAX_LENGTH}
                posterText={postPosterText}
                posterAspectRatio="9 / 16"
                posterMaxWidth={190}
                initialCover={postToEdit?.poster}
                onSave={onSavePost}
              />
            </ModalFade>
          )}
          {deletePostFade.rendered && (
            <ModalFade closing={deletePostFade.closing}>
              <ConfirmationModal
                shown
                title={postsText.deleteConfirm}
                acceptText={postsText.delete}
                onAccept={onConfirmDeletePost}
                onCancel={() => setPostToDelete(undefined)}
              />
            </ModalFade>
          )}
          {learnersPost && learnersPost.publishedCourseId && (
            <MicroreelLearnersModal
              groupTitle={learnersPost.title}
              publishedCourseId={learnersPost.publishedCourseId}
              onClose={() => setLearnersPost(undefined)}
            />
          )}
        </>
      );
    },
  ),
);

export default ProjectDetailsContainer;
